import React from 'react';
import clsx from 'clsx';
import { constrain, moduloClamp } from 'js/helpers/math';
import { GesturePlaneDirection, TouchGesture } from 'js/helpers/TouchGesture';
import styles from './GenericCarousel.module.css';
import { NaiveAnimator } from './NaiveAnimator';
import {
  fractionalIndexToAbsoluteIndex,
  itemPosition,
  offsetXToFractionalIndex,
} from './helpers';

export type ClickCallback = (event: React.SyntheticEvent) => void;
export type IndicatorClickCallback = (index: number) => void;

interface Props<ItemType> {
  /**
   * It is IMPORTANT to pass a positioningClassName that includes either `position: relative` or `absolute`.
   * This class also has to provide sizing css for the carousel. The size of the carousel is not allowed to
   * change without a resize of the window.
   */
  positioningClassName?: string;

  /**
   * An array of any type of data that will be used by renderItem to create a ReactNode.
   */
  items: ItemType[];

  /**
   * @param item The data for the item that was passed in with `items`
   */
  renderItem: (item: ItemType, index: number) => React.ReactNode;

  /**
   * @param numItems The number of items.
   * @param activeIndex The index of the currently active item.
   * @param itemNavigate A function to call to navigate to an item at a specific index.
   * @param isHover Whether the carousel is currrently hovered over.
   *        Might be used to determine if some navigation (such as chevrons) should be shown.
   * @param leftClickCallback A function to call to navigate to the previous item.
   * @param rightClickCallback A function to call to navigate to the next item.
   */
  renderNavigation: (
    numItems: number,
    activeIndex: number,
    itemNavigate: IndicatorClickCallback,
    isHover: boolean,
    leftClickCallback: ClickCallback,
    rightClickCallback: ClickCallback
  ) => React.ReactNode;

  /**
   * If true the navigation controls will be rendered outside of the element containing
   * the carousel itself. This allows the controls to be positioned outside of the carousel.
   *
   * Defaults to false.
   */
  navigationOutside?: boolean;

  /**
   * A function that will be called whenever an item is navigated to.
   */
  onItemChanged?: (itemIndex: number) => void;
}

interface State {
  fractionalIndex: number;
  containerWidth: number;
  isMouseOver: boolean;
}

/**
 * This component provides a generic way to implements carousels via render props.
 * Items in the carousel can be advanced via chevrons, pagination indicators and/or swiping
 * on touch devices.
 *
 * This is a newer, more generic implementation of a carousel with render props.
 */
export class GenericCarousel<ItemType> extends React.PureComponent<
  Props<ItemType>,
  State
> {
  private readonly INERTIA_PAGINATION_SPEED_THRESHOLD: number = 0.1;

  private containerRef: HTMLDivElement | null = null;

  private currentTouch: TouchGesture | undefined = undefined;

  private animator: NaiveAnimator;

  private isSawMouseMove = false;

  constructor(props: Props<ItemType>) {
    super(props);

    this.animator = new NaiveAnimator(this.onAnimatorUpdate);
  }

  public state: State = {
    fractionalIndex: 0,
    containerWidth: 0,
    isMouseOver: false,
  };

  public componentDidMount(): void {
    this.updateContainerWidth();

    window.addEventListener('resize', this.onResize);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.containerRef !== null) {
      this.containerRef.removeEventListener(
        'touchmove',
        this.onContainerTouchMove
      );
    }

    this.animator.stop();
  }

  public componentDidUpdate(prevProps: Props<ItemType>): void {
    // If the items have changed, ensure that we're not trying to
    // show an item that's past the last item.
    if (this.props.items.length !== prevProps.items.length) {
      const clampedValue = moduloClamp(
        this.state.fractionalIndex,
        0,
        this.props.items.length - 1
      );
      this.animator.setActualValue(clampedValue);
    }
  }

  // event handling & resizing
  private onResize = () => {
    this.updateContainerWidth();
  };

  private updateContainerWidth(): void {
    if (this.containerRef === null) {
      return;
    }

    this.setState({ containerWidth: this.containerRef.clientWidth });
  }

  private updateContainerRef = (ref: HTMLDivElement | null): void => {
    if (this.containerRef !== null) {
      this.containerRef.removeEventListener(
        'touchmove',
        this.onContainerTouchMove
      );
    }

    this.containerRef = ref;
    this.updateContainerWidth();

    if (this.containerRef !== null) {
      this.containerRef.addEventListener(
        'touchmove',
        this.onContainerTouchMove,
        { passive: false }
      );
    }
  };

  // animations
  private onAnimatorUpdate = (value: number, isComplete: boolean): void => {
    if (isComplete) {
      const clampedValue = moduloClamp(value, 0, this.props.items.length);
      this.animator.setActualValue(clampedValue);
      this.props.onItemChanged?.(clampedValue);
      return;
    }

    this.setState({ fractionalIndex: value });
  };

  // handle clicking
  private onIndicatorClick = (index: number): void => {
    this.animator.setDestinationValue(index);
  };

  private onLeftChevronClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    this.changeActiveImage(-1);
  };

  private onRightChevronClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    this.changeActiveImage(1);
  };

  private changeActiveImage(indexOffset: 1 | -1): void {
    const currentIndex = fractionalIndexToAbsoluteIndex(
      this.animator.getDestinationValue(),
      this.props.items.length
    );
    this.animator.setDestinationValue(currentIndex + indexOffset);
  }

  // handle hover

  // we are using mouseMove to basically achieve mouseOver on desktop only.
  // css hover is causing problems on android devices, where css hover and mouseEnter/Over events are simulated.
  // mouseMove is not simulated, and therefore can only happen with a pointer.
  // re-setting the state should be cheap in a PureComponent.
  // unfortunately mouseMove gets simulated when a click is happening, therefore we are checking
  // that the mouseMove event is fired twice before we assume a hover.
  // we are resetting this observation on mouseLeave and touchEnd.
  private onMouseMove = (): void => {
    if (this.isSawMouseMove) {
      this.setState({ isMouseOver: true });
    }

    this.isSawMouseMove = true;
  };

  private onMouseLeave = (): void => {
    this.isSawMouseMove = false;
    this.setState({ isMouseOver: false });
  };

  // handle touch dragging
  private onContainerTouchStart = (event: React.TouchEvent) => {
    if (event.targetTouches.length === 1) {
      const touch = event.targetTouches.item(0);
      this.currentTouch = new TouchGesture(touch, this.state.fractionalIndex);
    }
  };

  private onContainerTouchMove = (event: TouchEvent) => {
    if (this.currentTouch === undefined) {
      return;
    }

    if (this.props.items.length < 2) {
      // Don't allow dragging if there are not enough
      // items for navigation.
      return;
    }

    this.currentTouch.update(event.targetTouches);

    switch (this.currentTouch.getPlaneDirection()) {
      case GesturePlaneDirection.Horizontal:
        event.preventDefault();
        break;
      case GesturePlaneDirection.Vertical:
        this.endTouchGesture(0);
        break;
    }

    // needs to be re-checked, because endTouchGesture might have removed the currentTouch
    if (this.currentTouch === undefined) {
      return;
    }

    const fractionalIndexOffset = offsetXToFractionalIndex(
      this.currentTouch.getOffsetX(),
      this.state.containerWidth
    );
    // we do not want to allow to swipe more than one item with one swipe
    const constrainedFractionalIndexOffset = constrain(
      fractionalIndexOffset,
      -1,
      1
    );

    this.animator.setActualValue(
      this.currentTouch.getStartFractionalIndex() +
        constrainedFractionalIndexOffset
    );
  };

  private endTouchGesture(offsetX: number): void {
    this.isSawMouseMove = false;

    if (this.currentTouch === undefined) {
      return;
    }

    const touchSpeed = this.currentTouch.getSpeedX();
    if (touchSpeed === 0) {
      return;
    }

    if (Math.abs(touchSpeed) > this.INERTIA_PAGINATION_SPEED_THRESHOLD) {
      const offset = touchSpeed < 0 ? 1 : -1;
      this.changeActiveImage(offset);
    } else {
      const fractionalIndexOffset = offsetXToFractionalIndex(
        offsetX,
        this.state.containerWidth
      );
      // we do not want to allow to swipe more than one item with one swipe
      const contrainedFractionalIndexOffset = constrain(
        fractionalIndexOffset,
        -1,
        1
      );

      const newFractionalIndex: number = Math.round(
        this.currentTouch.getStartFractionalIndex() +
          contrainedFractionalIndexOffset
      );
      this.animator.setDestinationValue(newFractionalIndex);
    }

    this.currentTouch = undefined;
  }

  private onContainerTouchEnd = (event: React.TouchEvent): void => {
    if (this.currentTouch === undefined) {
      return;
    }

    this.currentTouch.update(event.changedTouches);

    if (this.currentTouch.containedIn(event.changedTouches)) {
      this.endTouchGesture(this.currentTouch.getOffsetX());
    }
  };

  private onContainerTouchCancel = (): void => {
    this.endTouchGesture(0);
  };

  // rendering
  private renderItem = (
    item: ItemType,
    index: number,
    isSingleItemDuplicate = false
  ): React.ReactNode => {
    let itemPositionValue: number;

    if (isSingleItemDuplicate) {
      itemPositionValue = itemPosition(
        index,
        this.state.fractionalIndex,
        this.state.containerWidth,
        2
      );
    } else {
      itemPositionValue = itemPosition(
        index,
        this.state.fractionalIndex,
        this.state.containerWidth,
        this.props.items.length
      );
    }

    return (
      <div
        key={index}
        className={styles.item}
        style={{
          transform: `translateX(${itemPositionValue}px)`,
        }}
      >
        {this.props.renderItem(item, index)}
      </div>
    );
  };

  private renderNavigation(): React.ReactNode {
    if (this.props.items.length < 2) {
      return null;
    }

    return this.props.renderNavigation(
      this.props.items.length,
      fractionalIndexToAbsoluteIndex(
        this.state.fractionalIndex,
        this.props.items.length
      ),
      this.onIndicatorClick,
      this.state.isMouseOver,
      this.onLeftChevronClick,
      this.onRightChevronClick
    );
  }

  public render(): React.ReactNode {
    return (
      <>
        <div
          ref={this.updateContainerRef}
          className={clsx(styles.container, this.props.positioningClassName)}
          onTouchStart={this.onContainerTouchStart}
          onTouchEnd={this.onContainerTouchEnd}
          onTouchCancel={this.onContainerTouchCancel}
          onMouseMove={this.onMouseMove}
          onMouseLeave={this.onMouseLeave}
        >
          {this.props.items.map((item, index) => this.renderItem(item, index))}
          {this.props.items.length === 1 &&
            this.renderItem(this.props.items[0], 1, true)}

          {!this.props.navigationOutside && this.renderNavigation()}
        </div>

        {this.props.navigationOutside && this.renderNavigation()}
      </>
    );
  }
}
